import { useState, type FormEvent } from 'react';
import { useAuth } from './AuthProvider';

const MIN_PASSWORD_LENGTH = 8;

export function ResetPasswordPage() {
    const { updatePassword } = useAuth();
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    async function handleSubmit(e: FormEvent) {
        e.preventDefault();
        setError(null);

        if (newPassword.length < MIN_PASSWORD_LENGTH) {
            setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setSubmitting(true);
        const { error: updateError } = await updatePassword(newPassword);
        setSubmitting(false);

        if (updateError) {
            setError(updateError.message);
        }
        // On success, AuthProvider clears the recovery flag and App.tsx
        // re-renders to the role-appropriate landing page.
    }

    return (
        <div className="min-h-screen flex flex-col bg-graphite-50 dark:bg-graphite-950">
            <div className="flex-1 flex items-center justify-center px-6">
                <div className="w-full max-w-sm">
                    <div className="mb-10">
                        <div className="flex items-baseline gap-3 mb-2">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-graphite-500">
                                PPC / Internal
                            </span>
                            <span className="h-px flex-1 bg-graphite-200 dark:bg-graphite-800" />
                        </div>
                        <h1 className="font-display font-bold text-3xl text-graphite-900 dark:text-graphite-50 tracking-tight">
                            Set Your Password
                        </h1>
                        <p className="font-mono text-xs text-graphite-500 mt-1">
                            Choose a password you will use to sign in.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label
                                htmlFor="new-password"
                                className="block font-mono text-[10px] uppercase tracking-[0.15em] text-graphite-600 dark:text-graphite-400 mb-2"
                            >
                                New Password
                            </label>
                            <input
                                id="new-password"
                                type="password"
                                required
                                autoComplete="new-password"
                                minLength={MIN_PASSWORD_LENGTH}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="w-full bg-white border border-graphite-300 text-graphite-900 placeholder-graphite-400 dark:bg-graphite-900 dark:border-graphite-700 dark:text-graphite-50 dark:placeholder-graphite-600 rounded px-3 py-2.5 focus:border-signal-500 focus:ring-0 focus:outline-none transition-colors"
                                placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                            />
                        </div>

                        <div>
                            <label
                                htmlFor="confirm-password"
                                className="block font-mono text-[10px] uppercase tracking-[0.15em] text-graphite-600 dark:text-graphite-400 mb-2"
                            >
                                Confirm Password
                            </label>
                            <input
                                id="confirm-password"
                                type="password"
                                required
                                autoComplete="new-password"
                                minLength={MIN_PASSWORD_LENGTH}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full bg-white border border-graphite-300 text-graphite-900 placeholder-graphite-400 dark:bg-graphite-900 dark:border-graphite-700 dark:text-graphite-50 dark:placeholder-graphite-600 rounded px-3 py-2.5 focus:border-signal-500 focus:ring-0 focus:outline-none transition-colors"
                                placeholder="Re-enter the same password"
                            />
                        </div>

                        {error ? (
                            <div className="border-l-2 border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-amber-600 dark:text-amber-400">
                                {error}
                            </div>
                        ) : null}

                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-signal-500 hover:bg-signal-400 disabled:bg-graphite-300 dark:disabled:bg-graphite-700 disabled:cursor-not-allowed text-graphite-950 disabled:text-graphite-500 dark:disabled:text-graphite-950 font-semibold py-2.5 rounded transition-colors"
                        >
                            {submitting ? 'Updating...' : 'Set Password'}
                        </button>
                    </form>

                    <div className="mt-10 font-mono text-[10px] text-graphite-400 dark:text-graphite-600 leading-relaxed">
                        Once set, you will be signed in automatically.
                    </div>
                </div>
            </div>

            <footer className="px-6 py-4 border-t border-graphite-200 dark:border-graphite-900 font-mono text-[10px] text-graphite-400 dark:text-graphite-600 flex items-center justify-between">
                <span>schedule.ppcconcrete.com</span>
                <span>v0.1</span>
            </footer>
        </div>
    );
}
